import { redirect } from 'react-router-dom';

// Dueño de la copia
import {
  assertDraftOwner,
  DraftOwnerMismatchError,
} from '../../../../../../utils/draftOwnership';
import { CAMPAIGN_EDIT_SCOPE } from '../../../../../../utils/draftScopes';

// Sentry
import * as Sentry from '@sentry/react';

// Constants
import { formOrderNaming } from '../../../../../../components/forms/NewWorkOrderForm/constants';

// GQL
import { client } from '../../../../../../gql/client';
import { SAVE_CAMPAIGN } from '../../../../../../gql/mutations/campaign';

// Types
import { SaveDataParams } from './types';
import { UNSPECIFIC } from '../../../../../../types/utils';

export const saveData = async ({ formData, urlCampaignId }: SaveDataParams) => {
  const id = formData.get('id');

  // Barrera: se guarda la campaña de la URL o no se guarda nada. Falla CERRADA:
  // sin `:id`, o con un `id` de formulario que no es el de la URL, se lanza
  // sin escribir ni encolar (antes mandaba el `id` de la copia de sesión).
  // La reproducción de la cola offline no pasa por aquí: reenvía la mutación
  // ya capturada.
  assertDraftOwner(CAMPAIGN_EDIT_SCOPE, urlCampaignId, { isReplay: false });
  if (!urlCampaignId || id?.toString() !== urlCampaignId) {
    const mismatch = new DraftOwnerMismatchError(
      CAMPAIGN_EDIT_SCOPE.name,
      id?.toString() ?? null,
      urlCampaignId ?? '(sin :id)'
    );
    Sentry.captureException(mismatch, {
      tags: { guard: `draft-owner-mismatch:${CAMPAIGN_EDIT_SCOPE.name}` },
    });
    throw mismatch;
  }

  const description = formData.get('description');
  const notes = formData.get('notes');
  const team = formData.get('team');
  const campaign = formData.get('campaign');
  const lane = formData.get('lane');
  const margin = formData.get('margin');
  const direction = formData.get('direction');
  const track = formData.get('track');
  const road = formData.get('road');
  const pkInit = formData.get('pkInit');
  const pkEnd = formData.get('pkEnd');
  const concession = formData.get('concession');
  const registerInitDate = formData.get('registerInitDate');
  const capitol = formData.get('capitol');
  const subCapitol = formData.get('subCapitol');
  const predefine = formData.get('predefine');
  const modelTypeId = formData.get('modelTypeId');

  try {
    const pkInitSplit = pkInit?.toString().replace(/ /g, '').split('+');
    const pkEndSplit = pkEnd?.toString().replace(/ /g, '').split('+');

    // Bloque C-3: SAVE_CAMPAIGN (update path con id) sustituye UPDATE_ORDER +
    // UPDATE_CAMPAIGN + UPDATE_CAMPAIGN_RELATIONSHIPS + UPDATE_ORDER_RELATIONSHIPS
    // por una mutación atómica vía $transaction Prisma. Backend actualiza Order
    // + Campaign con los mismos campos Tipo B coherentes — elimina el deadlock
    // P2034 que motivaba el skipOrderSync.
    if (
      registerInitDate || description || notes || pkInit || pkEnd ||
      campaign || lane || margin || direction || track ||
      concession || road || team ||
      capitol || subCapitol || predefine
    ) {
      await client.mutate({
        mutation: SAVE_CAMPAIGN,
        variables: {
          input: {
            // Idempotencia online: UUID estable por operación (RetryLink no duplica).
            clientOpId: crypto.randomUUID(),
            id: modelTypeId,
            registerInitDate: registerInitDate ? registerInitDate : undefined,
            description: description ? description : undefined,
            notes: notes ? notes : undefined,
            pkInitKm: pkInit ? Number(pkInitSplit?.[0]) : undefined,
            pkInitMeter: pkInit ? Number(pkInitSplit?.[1]) : undefined,
            pkEndKm: pkEnd ? Number(pkEndSplit?.[0]) : undefined,
            pkEndMeter: pkEnd ? Number(pkEndSplit?.[1]) : undefined,
            track: track ? track : undefined,
            direction: direction ? direction : UNSPECIFIC,
            margin: margin ? margin : UNSPECIFIC,
            lane: lane ? lane : undefined,
            concessionId: concession ? concession : undefined,
            roadId: road ? road : undefined,
            teamIds: team ? String(team).split(',').map((s) => s.trim()).filter(Boolean) : undefined,
            capitolId: capitol ? capitol : undefined,
            subCapitolId: subCapitol ? subCapitol : undefined,
            predefinedOrderId: predefine ? predefine : undefined,
            name: campaign ? campaign : undefined,
          },
        },
      });
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Campaign updated - error updating', err);
    // Re-lanzar: antes se tragaba el error Y SE REDIRIGÍA A ÉXITO igualmente
    // (navegación optimista incorrecta — clase del incidente
    // At.0001979.0008.02). Además esto revive el handler offline-queued del
    // action (C-7.5), que era código muerto porque este catch capturaba antes.
    throw err;
  }

  sessionStorage.removeItem(formOrderNaming.EDIT_CAMPAIGN_FORM);
  sessionStorage.removeItem(CAMPAIGN_EDIT_SCOPE.ownerKey);
  return redirect(`../../${id}`);
};
